import { COLORS, RESET_PILL } from './tokens';
import { formatSchoolYear } from './derive';

export type ChartPoint = { year: number; level: number };

type LineChartProps = {
  points: ChartPoint[];
  height: number;
  yMin?: number;
  yMax?: number;
  yTicks?: number[];
  threshold?: { value: number; label?: string };
  gapYear?: number;
  markerYear?: number;
  markerLabel?: string;
  compact?: boolean;
};

const WIDTH = 900;

export function LineChart({
  points,
  height,
  yMin = 1,
  yMax = 6,
  yTicks,
  threshold,
  gapYear,
  markerYear,
  markerLabel,
  compact = false,
}: LineChartProps) {
  const left = compact ? 22 : 34;
  const right = 14;
  const top = 14;
  const bottom = compact ? 18 : 24;
  const innerW = WIDTH - left - right;
  const innerH = height - top - bottom;

  const years = points.map((p) => p.year);
  const minYear = years.length ? Math.min(...years) : 0;
  const maxYear = years.length ? Math.max(...years) : 1;
  const span = maxYear - minYear || 1;

  const x = (year: number) => left + ((year - minYear) / span) * innerW;
  const y = (level: number) => top + (1 - (level - yMin) / (yMax - yMin)) * innerH;

  const ticks = yTicks ?? [yMin, (yMin + yMax) / 2, yMax];
  const polyline = points.map((p) => `${x(p.year)},${y(p.level)}`).join(' ');

  return (
    <svg
      viewBox={`0 0 ${WIDTH} ${height}`}
      width="100%"
      height={height}
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label="Proficiency trajectory"
    >
      {ticks.map((t) => (
        <g key={`tick-${t}`}>
          <line x1={left} y1={y(t)} x2={WIDTH - right} y2={y(t)} stroke={COLORS.hairlineSoft} />
          <text
            x={left - 6}
            y={y(t) + 3}
            textAnchor="end"
            fill={COLORS.muted}
            fontSize={compact ? 10 : 12}
            fontWeight={500}
          >
            {t.toFixed(1)}
          </text>
        </g>
      ))}

      {threshold && (
        <g>
          <line
            x1={left}
            y1={y(threshold.value)}
            x2={WIDTH - right}
            y2={y(threshold.value)}
            stroke={COLORS.navy}
            strokeWidth={1.5}
            strokeDasharray="5 4"
          />
          {threshold.label && (
            <text x={left + 4} y={y(threshold.value) - 6} fill={COLORS.navy} fontSize={12} fontWeight={700}>
              {threshold.label}
            </text>
          )}
        </g>
      )}

      {gapYear != null && gapYear >= minYear && gapYear <= maxYear && (
        <g>
          <line
            x1={x(gapYear)}
            y1={top}
            x2={x(gapYear)}
            y2={top + innerH}
            stroke={COLORS.hairline}
            strokeDasharray="4 4"
          />
          {!compact && (
            <text x={x(gapYear) + 5} y={top + innerH / 2} fill={COLORS.muted} fontSize={11} fontWeight={700}>
              WAIVED
            </text>
          )}
        </g>
      )}

      {markerYear != null && markerYear >= minYear && markerYear <= maxYear && (
        <g>
          <line
            x1={x(markerYear)}
            y1={top}
            x2={x(markerYear)}
            y2={top + innerH}
            stroke="rgba(8,8,27,0.4)"
          />
          {markerLabel && !compact && (
            <text
              x={x(markerYear)}
              y={top - 2}
              textAnchor="middle"
              fill={COLORS.muted}
              fontSize={10}
              fontWeight={700}
            >
              {markerLabel}
            </text>
          )}
        </g>
      )}

      {points.length > 1 && (
        <polyline
          points={polyline}
          fill="none"
          stroke={COLORS.navy}
          strokeWidth={2.5}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      )}

      {points.map((p, i) => {
        const isLast = i === points.length - 1;
        return (
          <circle
            key={p.year}
            cx={x(p.year)}
            cy={y(p.level)}
            r={isLast ? 6 : 4.5}
            fill={isLast ? COLORS.navy : '#FFFFFF'}
            stroke={COLORS.navy}
            strokeWidth={2}
          />
        );
      })}

      {points.map((p) => (
        <text
          key={`xlabel-${p.year}`}
          x={x(p.year)}
          y={height - 4}
          textAnchor="middle"
          fill={COLORS.muted}
          fontSize={compact ? 10 : 12}
          fontWeight={p.year === maxYear ? 700 : 500}
        >
          {compact ? `'${String(p.year).slice(2)}` : p.year}
        </text>
      ))}
    </svg>
  );
}

type ResetSplitChartProps = {
  prePoints: ChartPoint[];
  postPoints: ChartPoint[];
  resetYear: number;
  height: number;
  yMin?: number;
  yMax?: number;
  yTicks?: number[];
  threshold?: { value: number; label?: string };
  gapYear?: number;
};

const FUTURE_SLOTS = 2;

export function ResetSplitChart({
  prePoints,
  postPoints,
  resetYear,
  height,
  yMin = 1,
  yMax = 6,
  yTicks = [1, 2, 3, 4, 5, 6],
  threshold,
  gapYear,
}: ResetSplitChartProps) {
  const top = 30;
  const bottom = 22;
  const innerTop = top;
  const innerH = height - top - bottom;

  const pillW = 30;
  const pillGap = 26;
  const axisW = 32;
  const padInner = 16;

  const pillCenter = WIDTH / 2;
  const leftX1 = pillCenter - pillW / 2 - pillGap;
  const rightX0 = pillCenter + pillW / 2 + pillGap;

  const leftPlotL = axisW;
  const leftPlotR = leftX1 - padInner;
  const rightPlotL = rightX0 + axisW;
  const rightPlotR = WIDTH - padInner;

  const lastPostYear = postPoints.reduce((max, p) => Math.max(max, p.year), resetYear);
  const postEndYear = Math.max(resetYear + FUTURE_SLOTS, lastPostYear + 1);
  const postSlotYears: number[] = [];
  for (let year = resetYear; year <= postEndYear; year++) postSlotYears.push(year);
  const currentYear = postPoints.length ? lastPostYear : resetYear;

  const y = (level: number) => innerTop + (1 - (level - yMin) / (yMax - yMin)) * innerH;

  const scaleX = (year: number, years: number[], plotL: number, plotR: number) => {
    const min = Math.min(...years);
    const max = Math.max(...years);
    const span = max - min || 1;
    return plotL + ((year - min) / span) * (plotR - plotL);
  };
  const preYears = prePoints.map((p) => p.year);
  const preX = (year: number) => scaleX(year, preYears, leftPlotL, leftPlotR);
  const postX = (year: number) => scaleX(year, postSlotYears, rightPlotL, rightPlotR);

  const prePolyline = prePoints.map((p) => `${preX(p.year)},${y(p.level)}`).join(' ');
  const postPolyline = postPoints.map((p) => `${postX(p.year)},${y(p.level)}`).join(' ');

  const yLabels = (plotL: number, plotR: number) =>
    yTicks.map((t) => (
      <g key={`yt-${plotL}-${t}`}>
        <line x1={plotL} y1={y(t)} x2={plotR} y2={y(t)} stroke={COLORS.hairlineSoft} />
        <text x={plotL - 6} y={y(t) + 3} textAnchor="end" fill={COLORS.muted} fontSize={12} fontWeight={500}>
          {t.toFixed(1)}
        </text>
      </g>
    ));

  const dataCircle = (p: ChartPoint, xPos: number, filled: boolean, emphasized: boolean) => (
    <circle
      key={`pt-${filled ? 'post' : 'pre'}-${p.year}`}
      cx={xPos}
      cy={y(p.level)}
      r={emphasized ? 6 : 4.5}
      fill={filled ? COLORS.navy : '#FFFFFF'}
      stroke={COLORS.navy}
      strokeWidth={2}
    />
  );

  const xLabel = (year: number, xPos: number) => {
    const isCurrent = year === currentYear;
    return (
      <text
        key={`xl-${year}`}
        x={xPos}
        y={height - 4}
        textAnchor="middle"
        fill={isCurrent ? COLORS.navy : COLORS.muted}
        fontSize={12}
        fontWeight={isCurrent ? 700 : 500}
      >
        {formatSchoolYear(year)}
      </text>
    );
  };

  const panels = [
    { l: leftPlotL, r: leftPlotR },
    { l: rightPlotL, r: rightPlotR },
  ];

  const inRange = (year: number, years: number[]) =>
    years.length > 0 && year >= Math.min(...years) && year <= Math.max(...years);
  const waivedMarker = (xPos: number) => (
    <g>
      <line x1={xPos} y1={innerTop} x2={xPos} y2={innerTop + innerH} stroke={COLORS.hairline} strokeDasharray="4 4" />
      <text x={xPos + 5} y={innerTop + innerH / 2} fill={COLORS.muted} fontSize={11} fontWeight={700}>
        WAIVED
      </text>
    </g>
  );

  const pill = RESET_PILL.full;
  const pillH = Math.min(innerH * 0.62, 132);
  const pillY = innerTop + (innerH - pillH) / 2;

  return (
    <svg
      viewBox={`0 0 ${WIDTH} ${height}`}
      width="100%"
      height={height}
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label="Proficiency trajectory across the WIDA scale reset"
    >
      <text x={(leftPlotL + leftPlotR) / 2} y={16} textAnchor="middle" fill={COLORS.muted} fontSize={13} fontWeight={600}>
        {`Pre-${resetYear - 1}–${String(resetYear).slice(2)} WIDA Scale`}
      </text>
      <text x={(rightPlotL + rightPlotR) / 2} y={16} textAnchor="middle" fill={COLORS.navy} fontSize={13} fontWeight={700}>
        Current WIDA Scale
      </text>

      {yLabels(leftPlotL, leftPlotR)}
      {yLabels(rightPlotL, rightPlotR)}

      {threshold && (
        <g>
          {panels.map((p) => (
            <line
              key={`thr-${p.l}`}
              x1={p.l}
              y1={y(threshold.value)}
              x2={p.r}
              y2={y(threshold.value)}
              stroke={COLORS.navy}
              strokeWidth={1.5}
              strokeDasharray="5 4"
            />
          ))}
          {threshold.label && (
            <text x={leftPlotL + 4} y={y(threshold.value) - 6} fill={COLORS.navy} fontSize={12} fontWeight={700}>
              {threshold.label}
            </text>
          )}
        </g>
      )}

      {gapYear != null && gapYear < resetYear && inRange(gapYear, preYears) && waivedMarker(preX(gapYear))}
      {gapYear != null && gapYear >= resetYear && inRange(gapYear, postSlotYears) && waivedMarker(postX(gapYear))}

      {prePoints.length > 1 && (
        <polyline
          points={prePolyline}
          fill="none"
          stroke={COLORS.navy}
          strokeWidth={2.5}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      )}
      {postPoints.length > 1 && (
        <polyline
          points={postPolyline}
          fill="none"
          stroke={COLORS.navy}
          strokeWidth={2.5}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      )}

      {prePoints.map((p) => dataCircle(p, preX(p.year), false, p.year === currentYear))}
      {postPoints.map((p) => dataCircle(p, postX(p.year), true, p.year === currentYear))}

      {preYears.map((year) => xLabel(year, preX(year)))}
      {postSlotYears.map((year) => xLabel(year, postX(year)))}

      <g>
        <rect x={pillCenter - pillW / 2} y={pillY} width={pillW} height={pillH} rx={6} fill={pill.bg} />
        <text
          x={pillCenter}
          y={pillY + pillH / 2}
          textAnchor="middle"
          fill={pill.fg}
          fontSize={13}
          fontWeight={700}
          transform={`rotate(-90 ${pillCenter} ${pillY + pillH / 2})`}
        >
          Scale Reset
        </text>
      </g>
    </svg>
  );
}
